// use-braintree-applepay.ts

import { useEffect } from 'react'
import { applePay } from 'braintree-web'
import { useBraintree } from './use-braintree';
import { CARD_TYPES, CHECKOUT_ERROR_MESSAGES, PAYMENT_METHOD_IDS, SupportedApplePayVersion, applePayScript } from '../utils/constants'
import useCustomSitePreference from '../hooks/forms/useCustomSitePreference'
import { useCustomerStore, useGlobalStore, useOrderStore } from '../store/hooks/useStore'
import { PaymentMethodSetupData } from '../types/payments'
import { useBasketReview } from './use-basket-review';

export function useBraintreeApplePay() {
  const merchantLabel = useCustomSitePreference('BRAINTREE_APPLEPAY_MERCHANT_LABEL', 'iceland.co.uk')
  const { createBraintreeClientInstance } = useBraintree()
  const orderStore = useOrderStore()
  const { hasApplePayButtonRendered, setHasApplePayButtonRendered } = useGlobalStore()
  const { customerInfo } = useCustomerStore()
  const {reviewBasket} = useBasketReview()

  useEffect(() => {
    return () => {
      setHasApplePayButtonRendered(false)
    }
  }, [setHasApplePayButtonRendered])

  const initApplePayButton = async (config: PaymentMethodSetupData) => {
    if (!hasApplePayButtonRendered) {
      try {
        const { btClientInstance } = await createBraintreeClientInstance()
        const btApplePayInstance = await applePay.create({ client: btClientInstance })

        const containers = document.querySelectorAll('apple-pay-button')

        if (!containers.length) {
          orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.APPLE_PAY_CONTAINER_ERROR)
          return
        }

        containers.forEach((container) => {
          container.addEventListener('click', () => {

            const paymentRequest = btApplePayInstance.createPaymentRequest({
              total: {
                label: merchantLabel,
                amount: orderStore.amountToPay.toFixed(2),
              },
              requiredBillingContactFields: ['postalAddress'],
              billingContact: {
                phone: config.billingAddress?.phone || customerInfo?.phoneHome,
                emailAddress: config.billingAddress?.email || customerInfo?.email,
                givenName: config.billingAddress?.firstName || customerInfo?.firstName,
                familyName: config.billingAddress?.lastName || customerInfo?.lastName,
                postalCode: config.billingAddress?.postalCode || '',
                addressLines: [config.billingAddress?.address1 || '', config.billingAddress?.address2 || ''],
                locality: config.billingAddress?.city || '',
                countryCode: config.billingAddress?.countryCode || ''
              }
            })
            const session = new ApplePaySession(SupportedApplePayVersion, paymentRequest as ApplePayJS.ApplePayPaymentRequest)
            session.onvalidatemerchant = async (event) => {
              try {
                const merchantSession = await btApplePayInstance.performValidation({
                  validationURL: event.validationURL,
                  displayName: merchantLabel,
                })
                session.completeMerchantValidation(merchantSession)
              } catch (err) {
                session.abort()
                console.error(err)
                orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.APPLE_PAY_MERCHANT_ERROR)
              }
            }

            session.onpaymentauthorized = async (event) => {
              btApplePayInstance.tokenize(
                { token: event.payment.token },
                async (tokenizeErr, payload) => {
                  if (tokenizeErr || !payload) {
                    console.error(tokenizeErr)
                    orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.TOKENIZE_ERROR);
                    session.completePayment(ApplePaySession.STATUS_FAILURE)
                    return
                  }
                  try {
                    await config.submitOrderProcess(payload.nonce, CARD_TYPES.APPLE_PAY, config.billingAddress, PAYMENT_METHOD_IDS.APPLE_PAY)
                    session.completePayment(ApplePaySession.STATUS_SUCCESS)
                  } catch (orderErr) {
                    console.error(orderErr)
                    orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.ORDER_PROCESSING_ERROR)
                    session.completePayment(ApplePaySession.STATUS_FAILURE)
                  }
                }
              )
            }
            session.oncancel = (event) => {
              console.log('Apple Pay session was cancelled', event)
            }

            // Review the prices then begin the session if valid
            reviewBasket().then((value) => {
              if (!value) {
                session.abort()
                return
              }
              session.begin()
            })
          })
        })
        setHasApplePayButtonRendered(true)
      } catch (error) {
        console.error(error)
        orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.APPLE_PAY_INITIALIZATION_ERROR)
      }
    }
  }

  return {
    initApplePayButton
  }
}
